import { createApiRef } from '@backstage/core-plugin-api';
import { AxiosInstance } from 'axios';
import { AxiosInstanceProviderApi } from './axiosInstanceApi';
import { fetchEventSource } from '@microsoft/fetch-event-source';

export interface EllisMessage {
  role: string;
  content: string;
  attachments?: Attachment[];
}

export interface Attachment {
  id: string;
  name: string;
  type: 'image' | 'document';
  url: string;
  size?: number;
  base64Data?: string; // Base64 encoded file data
  mimeType?: string;
  sharepointFileId?: string; // SharePoint file ID from upload response
  sharepointUrl?: string; // SharePoint file URL from upload response
}

export interface EllisChatStreamPayload {
  message: EllisMessage[];
  tool?: string;
  metadata?: Record<string, unknown>;
}

export interface EllisConversation {
  id: string;
  title?: string;
  emailId?: string;
  createdAt?: string;
  updatedAt?: string;
}

// SharePoint Upload Types
export interface SharePointUploadPayload {
  user_email: string;
  file_name: string;
  file_content: string; // Base64 encoded
  file_type: string;
  subfolder_path: string;
}

export interface SharePointUploadResponse {
  success: boolean;
  file_url?: string;
  error?: string;
}

export interface EllisDntChatApi {
  chatStream: (
    data: EllisChatStreamPayload,
    accessToken: string,
    onStart: (response: Response) => void | Promise<void>,
    onStop: () => void,
    onData: (data: string) => void,
    onError: (error: unknown) => void,
    emailId?: string,
    abortSignal?: AbortSignal,
  ) => Promise<unknown>;
  createConversation: (data: Partial<EllisConversation>) => Promise<unknown>;
  getConversation: (conversationId: string) => Promise<unknown>;
  updateConversation: (
    conversationId: string,
    data: Partial<EllisConversation>,
  ) => Promise<unknown>;
  deleteConversation: (conversationId: string) => Promise<unknown>;
  uploadFileToSharePoint: (
    file: File,
    userEmail: string,
  ) => Promise<SharePointUploadResponse | null>;
}

export const ellisDntChatApiRef = createApiRef<EllisDntChatApi>({
  id: 'plugin.ellis-dnt-chat.service',
});

export class EllisDntChatApiImpl implements EllisDntChatApi {
  private readonly axiosInstancePromise: Promise<AxiosInstance>;

  constructor(axiosInstanceProviderApi: AxiosInstanceProviderApi) {
    this.axiosInstancePromise = axiosInstanceProviderApi.getInstance();
  }

  async chatStream(
    data: EllisChatStreamPayload,
    accessToken: string,
    onStart: (response: Response) => void | Promise<void>,
    onStop: () => void,
    onData: (data: string) => void,
    onError: (error: unknown) => void,
    emailId?: string,
    abortSignal?: AbortSignal,
  ): Promise<unknown> {
    const instance = await this.axiosInstancePromise;
    const baseUrl = instance.getUri();
    const tool = data.tool || 'jira';

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      };

      if (emailId) {
        headers['emailId'] = emailId;
      }

      // Always send JSON with embedded base64 file data in attachments
      const body = JSON.stringify({
        message: data.message,
        history: data.history,
        metadata: data.metadata,
      });

      return await fetchEventSource(
        `${baseUrl}turingbotservice/agent/chat-stream?tool=${tool}`,
        {
          method: 'POST',
          headers,
          body,
          signal: abortSignal,
          openWhenHidden: true,
          async onopen(response) {
            if (response.ok && response.status === 200) {
              await onStart(response);
              return;
            }

            if (
              response.status >= 400 &&
              response.status < 500 &&
              response.status !== 429
            ) {
              onError(response);
              onStop();
            }
          },
          onmessage(event) {
            if (event.data) {
              onData(event.data);
            }
          },
          onclose() {
            onStop();
          },
          onerror(error) {
            onError(error);
            onStop();
            throw error;
          },
        },
      );
    } catch (error) {
      onError(error);
      onStop();
      throw error;
    }
  }

  async createConversation(data: Partial<EllisConversation>): Promise<unknown> {
    const instance = await this.axiosInstancePromise;
    return instance
      .post('turingbotservice/conversations', data)
      .then(response => response.data);
  }

  async getConversation(conversationId: string): Promise<unknown> {
    const instance = await this.axiosInstancePromise;
    return instance
      .get(`turingbotservice/conversations/${conversationId}`)
      .then(response => response.data);
  }

  async updateConversation(
    conversationId: string,
    data: Partial<EllisConversation>,
  ): Promise<unknown> {
    const instance = await this.axiosInstancePromise;
    return instance
      .put(`turingbotservice/conversations/${conversationId}`, data)
      .then(response => response.data);
  }

  async deleteConversation(conversationId: string): Promise<unknown> {
    const instance = await this.axiosInstancePromise;
    return instance
      .delete(`turingbotservice/conversations/${conversationId}`)
      .then(response => response.data);
  }

  async uploadFileToSharePoint(
    file: File,
    userEmail: string,
  ): Promise<SharePointUploadResponse | null> {
    try {
      const instance = await this.axiosInstancePromise;
      const base64Content = await this.fileToBase64(file);

      const payload: SharePointUploadPayload = {
        user_email: userEmail,
        file_name: file.name,
        file_content: base64Content,
        file_type: file.type,
        subfolder_path: 'ellis-dt-chat-uploads',
      };

      const response = await instance.post<SharePointUploadResponse>(
        'turingbotservice/v2/sharepoint-upload',
        payload,
      );

      if (response.data.success && response.data.file_url) {
        return response.data;
      } else {
        console.error('SharePoint upload error:', response.data.error);
        return null;
      }
    } catch (error) {
      console.error('Error uploading file to SharePoint:', error);
      return null;
    }
  }

  private fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64Data = result.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };
}
